import { autoinject, computedFrom } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { AttachmentClientService } from "../../../../../../services/attachment/attachments-client.service";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { I18N } from "aurelia-i18n";
import { ValidationControllerFactory, Validator } from "aurelia-validation";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { AttachmentHelper } from "../../../../../../helpers/attachment-helper";
import { TextBox } from "../../../../../components/text-box/text-box";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { RightShipInspectionType } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { BaseRightshipEntityAttachmentDialog } from "../../entity-attachment/base-rightship-entity-attachment-dialog";
import { RightshipEntityAttachmentManager } from "../../rightship-entity-attachment/rightship-entity-attachment-manager";
import { CultureInfoService } from "../../../../../../services/internationalization/culture-info.service";
import { type CultureInfo } from "../../../../../../models/internationalization/culture-info.models";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { Reflection } from "../../../../../../utilities/reflection";
import { RightshipQiMessages } from "../../../../../../configuration/rightshipqi-event-messages.constants";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

@autoinject()
export class RightshipEntityAttachmentDialog extends BaseRightshipEntityAttachmentDialog {

    //File input element
    public fileInputEl: HTMLInputElement;
    //File input element - accept attribute binding
    public accept: string = null;
    //File input element - multiple binding
    public multiple: boolean = true;
    public maxFileSizeMB: number = 100;
    public extDotIndex: number;
    public editing: { [index: number]: boolean } = {};
    private pasteSubscription: Subscription;

    private attachmentMeta: AttachmentMetaModel = null;
    files: FileList;

    @computedFrom(Reflection.getPropertyPath<RightshipEntityAttachmentDialog>(z => z.cultureInfoService.cultures),
    Reflection.getPropertyPath<RightshipEntityAttachmentDialog>(z => z.cultureInfoService.cultures.length))
    public get cultures(): CultureInfo[] {
        return this.cultureInfoService.cultures;
    }

    private _culture: CultureInfo;
    
    public set culture(meta: CultureInfo)
    {
        this._culture = meta;
        this.updateAttachmentMeta(Reflection.getPropertyName<AttachmentMetaModel>(x => x.cultureCode), this._culture? this._culture.code: null);
    }

    @computedFrom(Reflection.getPropertyPath<RightshipEntityAttachmentDialog>(z => z._culture))
    public get culture(): CultureInfo
    {
        return this._culture;
    }

    constructor(element: Element, dialogClient: DialogClientService, notificationService: NotificationClientService,
        attachmentClientService: AttachmentClientService, userClaimsService: UserClaimsService, i18N: I18N,
        controllerFactory: ValidationControllerFactory, validator: Validator, private eventAggregator: EventAggregator,
        protected cultureInfoService: CultureInfoService, protected userService: UserClientService, protected configService: ConfigService) {
        super(element, dialogClient, notificationService, attachmentClientService, userClaimsService, i18N,
            controllerFactory, validator, configService);
    }

    @computedFrom(Reflection.getPropertyPath<RightshipEntityAttachmentDialog>(z => z._vm),
    Reflection.getPropertyPath<RightshipEntityAttachmentDialog>(z => z._vm.rootEntityType),
    Reflection.getPropertyPath<RightshipEntityAttachmentDialog>(z => z._vm.showAttachmentTypes),
    Reflection.getPropertyPath<RightshipEntityAttachmentDialog>(z => z._vm.attachmentTypeId),
    Reflection.getPropertyPath<RightshipEntityAttachmentDialog>(z => z._vm.rootItem),
    "_vm.rootItem.inspectionTypeId"
    )
    get showAttachmentTypes() {
        return ((this._vm.rootEntityType == EntityTypes.RightShipInspection && this._vm.rootItem["inspectionTypeId"] == RightShipInspectionType.RightShipInspection)
            || this._vm.showAttachmentTypes)
            && !this._vm.attachmentTypeId;
    }

    @computedFrom(Reflection.getPropertyPath<RightshipEntityAttachmentDialog>(z => z._vm),
    Reflection.getPropertyPath<RightshipEntityAttachmentDialog>(z => z._vm.showCulture))
    get showCulture()
    {
        return this._vm?.showCulture;
    }

    @computedFrom(Reflection.getPropertyPath<RightshipEntityAttachmentDialog>(z => z.showAttachmentTypes))
    get selectDefaultAttachmentType() {
        return !this.showAttachmentTypes;
    }

    detached() {
        if (this.pasteSubscription)
            this.pasteSubscription.dispose();
    }

    activate(vm: RightshipEntityAttachmentManager) {
        super.activate(vm);
        if (vm) {
            if (vm.attachmentTypeId) {
                this.attachmentTypeId = vm.attachmentTypeId;
            }
            if (vm["accept"]) {
                this.accept = vm["accept"];
            }
            if (vm["multiple"]) {
                this.multiple = vm["multiple"];
            }
            if (vm["maxFileSizeMB"]) {
                this.maxFileSizeMB = vm["maxFileSizeMB"];
            }

            if (!this.showAttachmentTypes) {
                super.rebuildValidationRules(false);
            }
        }
    }

    //Attached component lifecycle hook
    attached() {
        this.pasteSubscription = this.eventAggregator.subscribe(RightshipQiMessages.paste, (file) => {
            const newFileUri: EntityAttachmentDialogModel = {
                name: file.fileName,
                uri: file.source
            };

            const fileSource = file.source;
            const fileType = fileSource.substring(fileSource.indexOf(':')+1, fileSource.indexOf(';'));

            this.dataUri.push(newFileUri);

            //Get index of the file name extension's 'dot'
            const extDotIndex = file.fileName.lastIndexOf('.');
            this.name.push(file.fileName.substring(0, extDotIndex));

            const dataTransfer = new DataTransfer();

            if (this.files) {
                Array.from(this.files).forEach((file: File) => {
                    dataTransfer.items.add(file);
                });
            }

            dataTransfer.items.add([newFileUri].map(file => this.dataURLtoBlob(file.uri, fileType))[0]);
            this.files = dataTransfer.files;
            this.fileInputEl.files = dataTransfer.files;

            this.setSelectedFiles(this.files);
        });

        this.fileInputEl.addEventListener('change', (event) => {
            this.onFilesChanged(event);
        });
        this.culture = this.userService.getCurrentUserCulture();
    }

    public updateAttachmentMeta(property: string, value: any)
    {
        if(!this.attachmentMeta) this.attachmentMeta = <AttachmentMetaModel>{};
        this.attachmentMeta[property] = value;
        this.metaJson = JSON.stringify(this.attachmentMeta);
    }

    protected checkFilesType(files: FileList): boolean {
        let isValid = files?.length > 0;

        //if accept is not set no need to check files type
        if (!this.accept) return isValid;
        const types = this.accept.split(',').map(x => x.trim());

        if (!types || types.length == 0) return isValid;

        for (let i = 0; i < files.length; i++) {
            //this is for the .msg type as type property was empty for those
            if(!files[i].type) {
                const ext = "." + files[i].name.split('.').pop();
                isValid = isValid && types.indexOf(ext) > -1;
            } else {
                isValid = isValid && types.indexOf(files[i].type) > -1;
            }

            if (!isValid) break;
        }

        return isValid;
    }

    /**
     * Event handler when a file is added to the files input element
     * @param event Browser event
     */
    public onFilesChanged = (event) => {
        this.setFiles(event.target.files);
        if (this.files && this.checkFilesType(this.files)) {
            //Check file sizes
            for (let i = 0; i < this.files.length; i++) {
                const file = this.files[0];
                const filesize = ((file.size / 1024) / 1024);

                //If an uploaded file's size exceeds the maximum file size
                if (this.maxFileSizeMB && this.maxFileSizeMB > 0 && filesize > this.maxFileSizeMB) {
                    this.notificationService.show(`File size cannot be more than ${this.maxFileSizeMB}MB`, "", NotificationType.Error);
                    return;
                }
            }

            this.readURL(this.fileInputEl);
        } else {
            this.notificationService.show("Invalid File Type", "", NotificationType.Error);
        }
    };

    getFilesize(index) {
        return AttachmentHelper.formatBytes(this.files[index].size);
    }

    /**
     * Return file object's name
     * @param index Index of uploaded file
     */
    getFileName(index: number) {
        //If file name exists in list of file names, return it
        const fileName = this.name[index];
        if(fileName) return fileName;

        //Remove dot and extension from file name
        const extDotIndex = this.files[index].name.lastIndexOf('.')
        return this.files[index].name.substring(0, extDotIndex);
    }

    //When the 'Click to browse your computer' link is clicked
    public onBrowseLinkClicked() {
        this.fileInputEl.click();
    }

    public onDragOver($event: DragEvent) {
        $event.preventDefault();
    }

    public onDragDrop($event: DragEvent) {
        this.setFiles($event.dataTransfer.files);
        this.readURL(this.fileInputEl);
    }

    /**
     * Set the current files list
     * @param fileList File list
     */
    setFiles(fileList: FileList){
        let files = this.files==null ? [] : Array.from(this.files);
        files = files.concat(Array.from(fileList));
        const dataTransfer = new DataTransfer();
        files.forEach((file: File) => {
            dataTransfer.items.add(file); 
        });
        this.files = dataTransfer.files;
        this.fileInputEl.files = dataTransfer.files;
    }

    /**
     * Remove file uri from uploaded files
     * @param index
     */
    removeDataUri(index: number) {
        this.dataUri.splice(index, 1);
        this.name.splice(index, 1);      
        this.selectedFiles.splice(index,1);  
        const dataTransfer = new DataTransfer();
        this.selectedFiles.forEach(file => {
            dataTransfer.items.add(file);
        });
        this.files = dataTransfer.files;
        if (this.fileInputEl) {
            this.fileInputEl.value = "";
        }
    }

    /**
     * Edit uploaded file's name
     * @param textBox Textbox containing existing filename
     * @param index Uploaded file's index
     * @param focused If 'true', focus on the textbox, allowing file name to be changes
     */
    public editFileName(textBox: TextBox, index: number, focused: boolean = true) {
        this.editing[index] = !this.editing[index];
        if (!focused) {
            return;
        }
        this.getFileName(index);  
        if(textBox){
            textBox.focusTextBox();
            const input: HTMLInputElement = textBox.input;
            input.setSelectionRange(0, textBox.value.length, 'forward');
        }
    }

    protected async beforeSave(): Promise<boolean>
    {        
        if (this._vm.beforeSaveFunc)
        {
            const files = this.selectedFiles.length > 0 ? this.selectedFiles : this.dataUri.map(x => this.dataURLtoBlob(x.uri));
            
            return await this._vm.beforeSaveFunc(this._vm.rootItem, files, this.dataUri, this.culture != null? this.culture.code: null);
        }

        return true;
    }
}

export interface AttachmentMetaModel {
    cultureCode: string;
}

export interface EntityAttachmentDialogModel {
    attachmentTypeId?: number,
    uri: string,
    name: string
}